/**
 * Language roadmap graph: one node per published track with nodes, sequenced like intra-track graphs.
 * Ordering: Firestore learning_paths entries first when present; otherwise static curriculum fallback +
 * free fundamentals. Remaining published tracks follow alphabetically.
 *
 * Completion = all nodes inside that track done (same IDs as before: roadmap-lang-order-{trackId}).
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import * as roadmapService from '../services/roadmapService';
import { buildOrderedNodeGraph } from '../utils/roadmapGraph';
import {
  formatRoadmapTrackTitle,
  trackMatchesLanguage,
  formatLanguageDisplay,
  ROADMAP_SCOPE_LABEL,
  normalizeRoadmapLanguageKey,
  getTrackLanguageKey
} from '../utils/roadmapDisplay';
import { isRoadmapFreePublicTrack } from '../utils/roadmapSubscription';

const ENTRY_PREFIX = 'roadmap-lang-order-';

/** When path entries are missing, enforce this curriculum order (Firestore trackId). */
const CURRICULUM_FALLBACK_TRACK_IDS_BY_LANGUAGE = {
  python: [
    'python-programming-fundamentals',
    'python-data-science-ml',
    'python-deep-learning',
    'python-backend-web',
    'python-automation-scripting',
    'python-dsa-competitive'
  ],
  swift: [
    'swift-programming-fundamentals',
    'swift-ios-swiftui',
    'swift-uikit',
    'swift-concurrency',
    'swift-backend-vapor',
    'swift-dsa-competitive'
  ]
};

/** Languages whose curriculum order overrides Firestore path entry order until synced. */
const CURRICULUM_ORDER_OVERRIDE_LANGUAGES = new Set(['swift']);

/**
 * @param {Array<{ pathId: string, language?: string|null }>} paths
 * @param {string} langKey
 */
export function pickLearningPathForLanguage(paths, langKey) {
  const norm = normalizeRoadmapLanguageKey(langKey);
  const candidates = (paths || []).filter(
    (p) => normalizeRoadmapLanguageKey(p.language) === norm && p.pathId
  );
  if (!candidates.length) return null;
  const canonicalId = `learning-path-${norm}`;
  return candidates.find((p) => p.pathId === canonicalId) || candidates[0];
}

/**
 * @param {Array<{ trackId: string, title?: string, language?: string|null }>} withNodes
 * @param {Array<{ id: string, trackId: string, order?: number }>} pathEntries
 * @param {string|null} langKey
 */
export function orderTracksForLanguageRoadmap(withNodes, pathEntries, langKey) {
  const byId = new Map(withNodes.map((t) => [t.trackId, t]));
  const ordered = [];
  const seen = new Set();
  const normLang = normalizeRoadmapLanguageKey(langKey);
  const curriculumOrder = CURRICULUM_FALLBACK_TRACK_IDS_BY_LANGUAGE[normLang] || [];
  const useCurriculumOrder =
    curriculumOrder.length > 0 && CURRICULUM_ORDER_OVERRIDE_LANGUAGES.has(normLang);

  const appendByTrackId = (tid) => {
    if (!tid || seen.has(tid)) return;
    const t = byId.get(tid);
    if (t) {
      ordered.push(t);
      seen.add(tid);
    }
  };

  if (useCurriculumOrder) {
    for (const tid of curriculumOrder) {
      appendByTrackId(tid);
    }
  } else if (pathEntries?.length) {
    const sorted = [...pathEntries].sort((a, b) => {
      const oa = typeof a.order === 'number' ? a.order : Number(a.order) || 0;
      const ob = typeof b.order === 'number' ? b.order : Number(b.order) || 0;
      if (oa !== ob) return oa - ob;
      return String(a.id || '').localeCompare(String(b.id || ''));
    });
    for (const e of sorted) {
      appendByTrackId(e.trackId);
    }
  }

  /** Only when the path has no sheet-synced entries (legacy). Avoids duping fundamentals + reshuffling order. */
  if (!useCurriculumOrder && !pathEntries?.length) {
    const fundamentalsIds = withNodes.filter((t) => isRoadmapFreePublicTrack(t)).map((t) => t.trackId);
    const fallbackMerged = [...fundamentalsIds];
    for (const tid of curriculumOrder) {
      if (!fallbackMerged.includes(tid)) fallbackMerged.push(tid);
    }
    for (const tid of fallbackMerged) {
      appendByTrackId(tid);
    }
  }

  const remainder = withNodes
    .filter((t) => !seen.has(t.trackId))
    .sort((a, b) => (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' }));
  return [...ordered, ...remainder];
}

/**
 * @param {string|null} userId
 * @param {string|null} selectedLanguage - lowercase key, e.g. 'python' (null = idle)
 * @param {Array<{ trackId: string, title?: string, language?: string|null }>} tracks - published catalog
 */
export function useLanguageTrackOrderGraph(userId, selectedLanguage, tracks) {
  const tracksRef = useRef(tracks);
  tracksRef.current = tracks;

  const tracksCatalogSig =
    tracks.length === 0
      ? ''
      : tracks
          .map((t) => `${String(t.trackId)}:${getTrackLanguageKey(t)}`)
          .sort()
          .join('|');

  /**
   * Stable primitive for the fetch effect: same catalog content must not retrigger when `tracks`
   * is replaced with a new array reference on unrelated renders.
   */
  const langTracksFetchKey = useMemo(() => {
    const sel = selectedLanguage;
    const cur = tracksRef.current;
    if (sel == null || !String(sel).trim() || !cur?.length) return '';
    const ids = cur
      .filter((t) => trackMatchesLanguage(t, sel))
      .map((t) => String(t.trackId))
      .sort()
      .join('|');
    return `${normalizeRoadmapLanguageKey(String(sel))}|${ids}`;
  }, [tracksCatalogSig, selectedLanguage]);

  const [tracksWithNodes, setTracksWithNodes] = useState([]);
  const [pathEntries, setPathEntries] = useState([]);
  const [resolvedPathDoc, setResolvedPathDoc] = useState(null);
  const [completedEntryIds, setCompletedEntryIds] = useState([]);
  const [isLoadingEntries, setIsLoadingEntries] = useState(false);

  useEffect(() => {
    if (!langTracksFetchKey) {
      setTracksWithNodes([]);
      setPathEntries([]);
      setResolvedPathDoc(null);
      setCompletedEntryIds([]);
      setIsLoadingEntries(false);
      return;
    }
    const langTracksLocal = tracksRef.current.filter((t) => trackMatchesLanguage(t, selectedLanguage));
    if (!langTracksLocal.length) {
      setTracksWithNodes([]);
      setPathEntries([]);
      setResolvedPathDoc(null);
      setCompletedEntryIds([]);
      setIsLoadingEntries(false);
      return;
    }
    let cancelled = false;
    setIsLoadingEntries(true);
    (async () => {
      let entries = [];
      let pathMeta = null;
      try {
        const paths = await roadmapService.loadPublishedLearningPaths();
        if (cancelled) return;
        pathMeta = pickLearningPathForLanguage(paths, selectedLanguage);
        if (pathMeta?.pathId) {
          entries = await roadmapService.loadLearningPathEntries(pathMeta.pathId);
        }
      } catch {
        entries = [];
      }
      if (cancelled) return;
      setPathEntries(entries);
      if (pathMeta) {
        setResolvedPathDoc({
          pathId: pathMeta.pathId,
          title:
            pathMeta.title ||
            `${formatLanguageDisplay(selectedLanguage)} ${ROADMAP_SCOPE_LABEL}`,
          description: pathMeta.description || '',
          language: normalizeRoadmapLanguageKey(pathMeta.language) || selectedLanguage,
          status: pathMeta.status || 'published'
        });
      } else {
        setResolvedPathDoc(null);
      }

      const withNodes = [];
      const nodesByTrackId = new Map();
      const nodeResults = await Promise.all(
        langTracksLocal.map(async (t) => {
          try {
            const nodes = await roadmapService.loadTrackNodes(t.trackId);
            return { track: t, nodes };
          } catch {
            return { track: t, nodes: [] };
          }
        })
      );
      if (cancelled) return;
      for (const { track: t, nodes } of nodeResults) {
        if (nodes.length > 0) {
          withNodes.push(t);
          nodesByTrackId.set(t.trackId, nodes);
        }
      }
      if (cancelled) return;
      setTracksWithNodes(withNodes);

      const completionResults = await Promise.all(
        withNodes.map(async (t) => {
          const nodes = nodesByTrackId.get(t.trackId);
          if (!nodes?.length) return null;
          try {
            let prog = { completedNodeIds: [] };
            if (userId) {
              prog = await roadmapService.loadTrackProgress(userId, t.trackId);
            }
            const done = new Set(prog.completedNodeIds || []);
            const ordered = buildOrderedNodeGraph(nodes);
            const allDone = ordered.length > 0 && ordered.every((n) => done.has(n.id));
            return allDone ? `${ENTRY_PREFIX}${t.trackId}` : null;
          } catch {
            return null;
          }
        })
      );
      if (cancelled) return;
      const completed = completionResults.filter(Boolean);
      setCompletedEntryIds(completed);
      if (!cancelled) setIsLoadingEntries(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, langTracksFetchKey]);

  const orderedTracks = useMemo(
    () => orderTracksForLanguageRoadmap(tracksWithNodes, pathEntries, selectedLanguage),
    [tracksWithNodes, pathEntries, selectedLanguage]
  );

  const graphRows = useMemo(() => {
    return orderedTracks.map((t, i) => ({
      id: `${ENTRY_PREFIX}${t.trackId}`,
      parent: i === 0 ? null : `${ENTRY_PREFIX}${orderedTracks[i - 1].trackId}`,
      order: 0,
      title: formatRoadmapTrackTitle(selectedLanguage, t.title, t),
      description: '',
      snippetIds: [],
      trackId: t.trackId,
      pathId: resolvedPathDoc?.pathId || 'catalog-track-order',
      entryId: `${ENTRY_PREFIX}${t.trackId}`
    }));
  }, [orderedTracks, resolvedPathDoc?.pathId, selectedLanguage]);

  const orderedEntries = useMemo(() => buildOrderedNodeGraph(graphRows), [graphRows]);

  const entryById = useMemo(() => {
    const m = new Map();
    orderedEntries.forEach((r) => m.set(r.id, r));
    return m;
  }, [orderedEntries]);

  const currentEntryId = useMemo(() => {
    const done = new Set(completedEntryIds);
    const first = orderedEntries.find((n) => !done.has(n.id));
    return first ? first.id : null;
  }, [orderedEntries, completedEntryIds]);

  const pathCompletionRatio = useMemo(() => {
    const total = orderedEntries.length;
    if (!total) return { completed: 0, total: 0 };
    return { completed: completedEntryIds.length, total };
  }, [orderedEntries.length, completedEntryIds]);

  const pathDoc = useMemo(() => {
    if (selectedLanguage == null) return null;
    if (resolvedPathDoc) return resolvedPathDoc;
    return {
      pathId: 'catalog-track-order',
      title: `${formatLanguageDisplay(selectedLanguage)} ${ROADMAP_SCOPE_LABEL}`,
      description: '',
      language: selectedLanguage,
      status: 'published'
    };
  }, [selectedLanguage, resolvedPathDoc]);

  const unlockedEntryIds = useMemo(() => {
    const set = new Set();
    orderedEntries.forEach((n) => set.add(n.id));
    return set;
  }, [orderedEntries]);

  return {
    pathDoc,
    pathEntries,
    orderedTracks,
    orderedEntries,
    entryById,
    completedEntryIds,
    unlockedEntryIds,
    currentEntryId,
    pathCompletionRatio,
    isLoadingPaths: false,
    isLoadingEntries,
    error: null
  };
}
